// TabularInvoices.tsx
import React from 'react';
import { Tooltip } from 'react-tooltip';
import { KTSVG } from '../../../_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import TableHeading from '../../../components/tableHeading/TableHeading';
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu';
import TextField from '../../../components/textFields/TextField';
import { constraintConfig } from '../../../constraintConfig';
import labelKey from '../../localization/label.json';
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp';

// Define field types
type FieldType =
    | 'text'
    | 'currency'
    | 'date'
    | 'datetime'
    | 'status'
    | 'source'
    | 'user';

interface ColumnConfig {
    id: string;
    label: string;
    sortable?: boolean;
    className?: string;
    type?: FieldType;
    render?: (data: any, index?: number) => React.ReactNode;
    config?: {
        dateFormat?: string;
        currencyCode?: string;
        statusMapping?: Record<number, { label: string; className: string }>;
    };
}

interface TabularInvoicesProps {
    data: any[];
    isLoading: boolean;
    onViewReport?: (expenseId: number) => void;
    onViewLineItem?: (expenseItemId: number) => void;
    onViewUsedLineItem?: (expenseItemId: number) => void;
    onRetry?: (invoiceDataDraftId: number) => void;
    onTriggerExpenseReport?: (expenseId: number) => void;
    onTriggerItemsById?: (expenseId: number) => void;
    onTriggerItemById?: (expenseItemId: number) => void;
    setShowExpenseItemDetailModal?: (show: boolean) => void;
    setAttachmentDeleteData?: (id: number) => void;
    retryInvoiceHandler?: () => void;
    expenseDetailHandler?: () => void;
    onRowClick?: (data: any) => void;
    selectedRowId?: any;
    onClearFilters?: () => void;
    filterCount?: number;
    onDelete?: (id: number) => void; // Added delete handler
}

// Helper function to get status badge
const getStatusBadge = (ocrReadStatus: number) => {
    switch (ocrReadStatus) {
        case 0:
            return <span className="badge badge-primary fs-7 fw-semibold">{labelKey.processing}... <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>;
        case 1:
            return <span className="badge badge-success fs-7 fw-semibold">{labelKey.readyToUse}</span>;
        case 2:
            return <span className="badge badge-danger fs-7 fw-semibold">{labelKey.failed}</span>;
        case 3:
            return <span className="badge badge-secondary fs-7 fw-bold">{labelKey.attached}</span>;
        case 4:
            return <span className="badge badge-success fs-7 fw-bold">{labelKey.submitted}</span>;
        case 5:
            return <span className="badge badge-warning fs-7 fw-bold">{labelKey.alreadyUsed}</span>;
        case 6:
            return <span className="badge badge-info fs-7 fw-bold">{labelKey.futureReceipt}</span>;
        case 7:
            return <span className="badge badge-info fs-7 fw-bold">{labelKey.cCReceipt}</span>;
        default:
            return null;
    }
};

// Helper function to get source icon
const getSourceIcon = (recordSourceID: number) => {
    switch (recordSourceID) {
        case constraintConfig.recordSourceId.WebApp:
            return <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1' data-tooltip-id="web-source" data-tooltip-content='Website' />;
        case constraintConfig.recordSourceId.Mobile:
            return <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1' data-tooltip-id="mobile-source" data-tooltip-content='Mobile' />;
        case constraintConfig.recordSourceId.BankFeed:
            return <KTSVG path="/media/icons/figmaIcons/Bank.svg" className='svg-icon-1' data-tooltip-id="bank-source" data-tooltip-content='Bank' />;
        case constraintConfig.recordSourceId.GorillaExpense:
            return <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1 ms-4' data-tooltip-id="gorilla-source" data-tooltip-content='Gorilla Expense' />;
        default:
            return <span className="text-muted">N/A</span>;
    }
};

const TabularInvoices: React.FC<TabularInvoicesProps> = ({
    data,
    isLoading,
    onViewReport,
    onViewLineItem,
    onViewUsedLineItem,
    onRetry,
    onTriggerExpenseReport,
    onTriggerItemsById,
    onTriggerItemById,
    setShowExpenseItemDetailModal,
    setAttachmentDeleteData,
    retryInvoiceHandler,
    expenseDetailHandler,
    onRowClick,
    selectedRowId,
    onClearFilters,
    filterCount = 0,
    onDelete, // Destructure the new prop
}) => {
    // Column configuration with types
    const columns: ColumnConfig[] = [
        {
            id: 'orgUserName',
            label: "User",
            sortable: true,
            className: "w-150px text-center",
            type: 'user'
        },
        {
            id: 'trackingId',
            label: "Tracking ID",
            sortable: true,
            className: "w-150px text-center",
            render: (dataItem) => renderTrackingId(dataItem)
        },
        {
            id: 'vendorName',
            label: "Vendor",
            sortable: true,
            className: "w-150px text-center",
            type: 'text'
        },
        {
            id: 'ocrReadStatus',
            label: "Receipt Status",
            sortable: true,
            className: "w-150px text-center",
            type: 'status'
        },
        {
            id: 'invoiceDate',
            label: "Receipt Date",
            sortable: true,
            className: "w-150px text-center",
            type: 'date'
        },
        {
            id: 'createdDT',
            label: "Receipt Uploaded Date",
            sortable: true,
            className: "w-150px text-center",
            type: 'datetime'
        },
        {
            id: 'expenseItemDate',
            label: "Expense Item Date",
            sortable: true,
            className: "w-150px text-center",
            type: 'date'
        },
        {
            id: 'amount',
            label: "Receipt Amount",
            sortable: true,
            className: "w-150px text-end", // Changed to text-end to match body
            type: 'currency'
        },
        {
            id: 'expenseItemAmount',
            label: "Item Amount",
            sortable: true,
            className: "w-150px text-end", // Changed to text-end to match body
            type: 'currency'
        },
        {
            id: 'invoiceNo',
            label: "Receipt Number",
            sortable: true,
            className: "w-150px text-center",
            type: 'text'
        },
        {
            id: 'recordSourceID',
            label: "Item Source",
            sortable: true,
            className: "w-100px text-center",
            type: 'source'
        },
        {
            id: 'actions',
            label: labelKey.actions,
            render: (dataItem) => renderActions(dataItem)
        }
    ];

    const defaultHiddenColumns: string[] = [];
    const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(defaultHiddenColumns);

    const handleToggleColumn = (columnId: string) => {
        if (hiddenColumns.includes(columnId)) {
            setHiddenColumns(hiddenColumns.filter((id) => id !== columnId));
        } else {
            setHiddenColumns([...hiddenColumns, columnId]);
        }
    };
    // Action renderer with updated delete functionality
    const renderTrackingId = (dataItem: any) => {
        return (
            <div className="d-flex flex-column align-items-center">
                <span className="text-primary fw-bold">{dataItem?.trackingId || 'N/A'}</span>
                <span className="fs-7">{dataItem?.category || ''}</span>
            </div>
        );
    };
    // Action renderer with updated delete functionality
    const renderActions = (dataItem: any) => {
        return (
            <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                {/* View Report Button */}
                {(dataItem?.ocrReadStatus === 3 || dataItem?.ocrReadStatus === 4) && dataItem.trackingId && (
                    <button
                        className='btn btn-sm btn-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            if (expenseDetailHandler) expenseDetailHandler();
                            if (onTriggerExpenseReport && dataItem?.expenseId)
                                onTriggerExpenseReport(dataItem?.expenseId);
                            if (onTriggerItemsById && dataItem?.expenseId)
                                onTriggerItemsById(dataItem?.expenseId);
                        }}
                    >
                        View Report
                    </button>
                )}

                {/* View Line Item Button */}
                {(dataItem?.ocrReadStatus === 3 || dataItem?.ocrReadStatus === 4) && dataItem.category && (
                    <button
                        className='btn btn-sm btn-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            if (setShowExpenseItemDetailModal) setShowExpenseItemDetailModal(true);
                            if (onTriggerItemById && dataItem?.expenseItemId)
                                onTriggerItemById(dataItem?.expenseItemId);
                        }}
                    >
                        View Line Item
                    </button>
                )}

                {/* View Used Line Item Button */}
                {dataItem?.ocrReadStatus === 5 && (
                    <button
                        className='btn btn-sm btn-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            if (setShowExpenseItemDetailModal) setShowExpenseItemDetailModal(true);
                            if (onTriggerItemById && dataItem?.usedExpenseItemId)
                                onTriggerItemById(dataItem?.usedExpenseItemId);
                        }}
                    >
                        View Used Line Item
                    </button>
                )}

                {/* Retry Button */}
                {dataItem?.ocrReadStatus === 2 && dataItem?.noOfRetry <= 2 && (
                    <button
                        className='btn btn-sm btn-warning'
                        onClick={(e) => {
                            e.stopPropagation();
                            if (setAttachmentDeleteData) setAttachmentDeleteData(dataItem?.invoiceDataDraftId);
                            if (retryInvoiceHandler) retryInvoiceHandler();
                        }}
                    >
                        Retry ({dataItem?.noOfRetry})
                    </button>
                )}

                {/* Delete Button - Updated with onDelete prop */}
                {((dataItem?.expenseItemId === null) || (dataItem.ocrReadStatus !== 3 && dataItem.ocrReadStatus !== 4)) && (
                    <button
                        className='btn btn-sm btn-danger'
                        onClick={(e) => {
                            e.stopPropagation();
                            // Use onDelete prop if available, otherwise fallback to setAttachmentDeleteData
                            if (onDelete) {
                                onDelete(dataItem?.invoiceDataDraftId);
                            } else if (setAttachmentDeleteData) {
                                setAttachmentDeleteData(dataItem?.invoiceDataDraftId);
                            }
                        }}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    };

    // Smart render function based on column type
    const renderCellContent = (column: ColumnConfig, dataItem: any, index: number) => {
        // If custom render is provided, use it (this handles actions)
        if (column.render) {
            return column.render(dataItem, index);
        }

        const value = dataItem?.[column.id];

        // Return null/empty for undefined values
        if (value === undefined || value === null) {
            return <span className="text-muted">N/A</span>;
        }

        switch (column.type) {
            case 'user':
                return (
                    <div className="d-flex flex-column align-items-center">
                        <span className="text-primary fw-bold">{dataItem?.orgUserId || ''}</span>
                        <span className="fs-7">{dataItem?.orgUserName || 'N/A'}</span>
                    </div>
                );

            case 'text':
                return <span>{value}</span>;

            case 'currency':
                return <CurrencyComp amount={value} />;

            case 'date':
                return <DateTimeComp formattedDate={value} />;

            case 'datetime':
                return <DateTimeComp formattedDate={value} />;

            case 'status':
                return getStatusBadge(value);

            case 'source':
                return getSourceIcon(value);

            default:
                return <span>{value}</span>;
        }
    };

    const getCellClassName = (column: ColumnConfig) => {
        if (column.id === 'actions') {
            return 'text-end pe-3';
        }
        if (column.type === 'currency') {
            return 'text-end';
        }
        return 'text-center';
    };

    const getHeaderClassName = (column: ColumnConfig) => {
        // Use the same logic as getCellClassName for consistency
        if (column.id === 'actions') {
            return 'text-end rounded-end pe-3 border-0';
        }
        if (column.type === 'currency') {
            return 'text-end';
        }
        // If column has a specific className, use it, otherwise default to text-center
        return column.className || 'text-center';
    };

    const visibleColumns = columns.filter(col => !hiddenColumns.includes(col.id));

    // Render No Records Found with enhanced UI
    const renderNoRecords = () => (
        <tr>
            <td colSpan={visibleColumns.length}>
                <div className="text-center py-8">
                    <div className="mb-4">
                        <div className="d-flex justify-content-center">
                            <div className="w-100px h-100px d-flex align-items-center justify-content-center rounded-circle bg-light-primary">
                                <i className="fa fa-inbox text-primary fs-1"></i>
                            </div>
                        </div>
                    </div>

                    <h5 className="text-gray-800 fw-bold mb-2">
                        No matching records found
                    </h5>

                    {filterCount > 0 && onClearFilters && (
                        <div className="d-flex flex-column align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-gray-400">Try different filters or</span>
                                <button
                                    className="btn btn-sm btn-primary rounded-pill px-4"
                                    onClick={onClearFilters}
                                >
                                    <i className="fa fa-times me-2"></i>
                                    Clear All Filters
                                </button>
                            </div>
                        </div>
                    )}

                    {filterCount === 0 && (
                        <div className="d-flex align-items-center justify-content-center gap-2 text-gray-400">
                            <span>Try adjusting your filters or search criteria</span>
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );

    return (
        <div className="col-md-12">
            <div className='table-responsive table-height'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                        <tr className='fw-bold text-muted'>
                            {visibleColumns.map((column) => (
                                column.id === 'actions' ? (
                                    <th 
                                        key={column.id} 
                                        className='text-end rounded-end pe-3 border-0' 
                                        style={{ minWidth: '200px' }}
                                    >
                                        {labelKey.actions}
                                        <TableSettingMenu>
                                            {columns.filter(col => col.id !== 'actions').map((col) => (
                                                <TextField
                                                    key={col.id}
                                                    rightLabel={col.label}
                                                    type="checkbox"
                                                    onChange={() => handleToggleColumn(col.id)}
                                                    checked={!hiddenColumns.includes(col.id)}
                                                    fieldClass='mb-4'
                                                />
                                            ))}
                                        </TableSettingMenu>
                                    </th>
                                ) : (
                                    <TableHeading
                                        key={column.id}
                                        label={column.label}
                                        sortedColumn={""}
                                        sortOrder={""}
                                        handleSort={column.sortable ? () => { } : undefined}
                                        columnId={column.id}
                                        className={getHeaderClassName(column)}
                                    />
                                )
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading && (
                            <>
                                {data?.length > 0 ? (
                                    data.map((dataItem: any, index: any) => (
                                        <tr
                                            key={index}
                                            className={`cursor-pointer ${selectedRowId === dataItem?.invoiceDataDraftId ? 'table-primary' : ''}`}
                                            onClick={() => onRowClick && onRowClick(dataItem)}
                                        >
                                            {visibleColumns.map((column) => (
                                                <td key={column.id} className={getCellClassName(column)}>
                                                    {renderCellContent(column, dataItem, index)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    renderNoRecords()
                                )}
                            </>
                        )}
                        {isLoading && (
                            <tr>
                                <td colSpan={visibleColumns.length} className="text-center">
                                    <LoadingComponent />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TabularInvoices;